import { panelHeaderControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import {
  SETTINGS_TAB_ICONS,
  settingsTabPath,
  useSettingsMenuTabs,
} from "@/views/Settings/settingsNav.ts";
import { Settings } from "lucide-react";
import { useLocation } from "wouter";

/**
 * The way into settings from an aside's footer: a gear opening the whole
 * settings menu, each row landing on that tab of the settings screen.
 *
 * The rows come from {@link useSettingsMenuTabs}: the settings screen's own
 * list, minus the destinations the sidebar and the mobile nav menu already name.
 * Gating stays shared with that screen, so a gated tab cannot show up in one and
 * not the other.
 *
 * Icon-only trigger, so the tooltip copy is also its accessible name — a bare
 * gear is otherwise unnamed to a screen reader.
 *
 * Desktop's way in only. A phone reaches settings from the footer row of its
 * nav menu, under the destinations it lists, rather than from a second control
 * in a header with room for one.
 */
export const SettingsNavButton = () => {
  const [, setLocation] = useLocation();
  const tabs = useSettingsMenuTabs();

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={panelHeaderControlClass}
              aria-label="Settings"
              data-statsig-name="nav-settings"
            >
              <Settings />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>Settings</TooltipContent>
      </Tooltip>
      {/* Opens upward and right-aligned, so it stays inside the sidebar it is
          anchored in rather than spilling across the canvas. Taller than the
          default cap: the list is every settings tab, and scrolling a menu beats
          hiding half of what the gear now promises. */}
      <DropdownMenuContent align="end" side="top" className="max-h-[70vh] w-56 overflow-y-auto">
        {tabs.map((tab) => {
          const Icon = SETTINGS_TAB_ICONS[tab.value];
          return (
            <DropdownMenuItem
              key={tab.value}
              onSelect={() => setLocation(settingsTabPath(tab.value))}
              data-statsig-name={`nav-settings-${tab.value}`}
            >
              <Icon className="size-4 text-muted-foreground" />
              {tab.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
